import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Zap, Flame, PlayCircle, Trophy, CheckCircle2, Loader2, Lock, Sparkles, Star } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { powerRewardsStatus, powerCheckin, powerWatchAd, powerAchievements } from "@/lib/powerApi";

export default function PowerRewards() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [ach, setAch] = useState(null);
  const [busy, setBusy] = useState(null); // 'checkin' | 'ad' | null
  const [tick, setTick] = useState(0);

  const reload = async () => {
    try {
      const [s, a] = await Promise.all([powerRewardsStatus(), powerAchievements()]);
      setStatus(s); setAch(a);
    } catch (e) {
      if (e.response?.status === 401) nav("/login?next=/power/rewards");
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/power/rewards"); return; }
    reload();
  // eslint-disable-next-line
  }, [loading, user]);

  // Live-tick the cooldown down to 0
  useEffect(() => {
    if (!status?.ad_cooldown_remaining_sec) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [status?.ad_cooldown_remaining_sec]);

  const cooldown = Math.max(0, (status?.ad_cooldown_remaining_sec || 0) - tick);

  const doCheckin = async () => {
    setBusy("checkin");
    try {
      const r = await powerCheckin();
      toast.success(`Checked in! Streak: ${r.streak_days} day${r.streak_days === 1 ? "" : "s"} · +${r.xp_gained} XP`);
      setTick(0); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't check in");
    } finally { setBusy(null); }
  };

  const doAd = async () => {
    if (cooldown > 0 || (status?.ads_watched_today || 0) >= (status?.ads_daily_limit || 0)) return;
    setBusy("ad");
    // Simulated 5s countdown before granting the reward
    let t = 5;
    const to = toast.loading(`Ad playing… ${t}s`);
    const int = setInterval(() => {
      t -= 1;
      if (t > 0) toast.loading(`Ad playing… ${t}s`, { id: to });
    }, 1000);
    setTimeout(async () => {
      clearInterval(int);
      try {
        const r = await powerWatchAd();
        toast.success(`+${r.xp_gained} XP · balance updated`, { id: to });
        setTick(0); reload();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Try again later", { id: to });
      } finally { setBusy(null); }
    }, 5000);
  };

  if (!status || !ach) return <div className="py-16 text-center text-[#0A1A2F]/50">Loading rewards…</div>;

  const level = status.level;
  const into = status.xp_into_level;
  const per = status.xp_per_level;
  const pct = Math.round(100 * into / per);
  const canAd = cooldown === 0 && (status.ads_watched_today || 0) < (status.ads_daily_limit || 0);

  return (
    <div data-testid="power-rewards" className="pb-6 space-y-5">
      {/* XP header */}
      <section className="rounded-3xl bg-gradient-to-br from-[#1E88FF] via-[#1E88FF] to-[#0A1A2F] text-white p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
            <Zap className="w-3 h-3" fill="white" /> Level {level}
          </div>
          <div className="text-4xl sm:text-5xl font-black tracking-tight" data-testid="rewards-xp">
            {status.xp} <span className="text-white/60 text-2xl">XP</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-white/15 overflow-hidden">
            <div className="h-full rounded-full bg-white transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-white/70 text-xs mt-1">{per - into} XP to level {level + 1}</div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <MiniStat label="Streak" value={`${status.streak_days}d`} icon={<Flame className="w-4 h-4" />} testId="rewards-streak" />
            <MiniStat label="Ads today" value={`${status.ads_watched_today}/${status.ads_daily_limit}`} icon={<PlayCircle className="w-4 h-4" />} />
            <MiniStat label="Badges" value={`${ach.unlocked_count}/${ach.total}`} icon={<Trophy className="w-4 h-4" />} />
          </div>
        </div>
      </section>

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Daily check-in */}
        <div className="bg-white rounded-2xl border border-[#E4EAF3] p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[#22C55E]/10 text-[#22C55E] flex items-center justify-center"><Flame className="w-5 h-5" /></div>
            <div>
              <div className="font-black text-[#0A1A2F]">Daily check-in</div>
              <div className="text-xs text-[#0A1A2F]/60">Come back every day to grow your streak</div>
            </div>
          </div>
          <button data-testid="rewards-checkin-btn" onClick={doCheckin}
            disabled={!status.checkin_available || busy === "checkin"}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#22C55E] to-[#1E88FF] text-white font-black py-3 rounded-full disabled:opacity-50">
            {busy === "checkin" ? <Loader2 className="w-4 h-4 animate-spin" /> : status.checkin_available ? <><Sparkles className="w-4 h-4" /> Claim today</> : <><CheckCircle2 className="w-4 h-4" /> Already claimed</>}
          </button>
        </div>

        {/* Rewarded ad */}
        <div className="bg-white rounded-2xl border border-[#E4EAF3] p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[#1E88FF]/10 text-[#1E88FF] flex items-center justify-center"><PlayCircle className="w-5 h-5" /></div>
            <div>
              <div className="font-black text-[#0A1A2F]">Watch a short ad</div>
              <div className="text-xs text-[#0A1A2F]/60">Optional — earn a small bonus for your time</div>
            </div>
          </div>
          <button data-testid="rewards-ad-btn" onClick={doAd} disabled={!canAd || busy === "ad"}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1E88FF] to-[#0A1A2F] text-white font-black py-3 rounded-full disabled:opacity-50">
            {busy === "ad" ? <Loader2 className="w-4 h-4 animate-spin" /> :
             cooldown > 0 ? <><Lock className="w-4 h-4" /> Cooldown {cooldown}s</> :
             (status.ads_watched_today >= status.ads_daily_limit) ? <><Lock className="w-4 h-4" /> Daily limit hit</> :
             <><PlayCircle className="w-4 h-4" /> Watch ad</>}
          </button>
        </div>
      </div>

      {/* Achievements */}
      <section data-testid="rewards-achievements" className="bg-white rounded-2xl border border-[#E4EAF3] p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-lg bg-[#FBBF24]/15 text-[#F59E0B] flex items-center justify-center"><Trophy className="w-5 h-5" /></div>
          <div>
            <div className="font-black text-[#0A1A2F]">Achievements</div>
            <div className="text-xs text-[#0A1A2F]/60">{ach.unlocked_count} of {ach.total} unlocked</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {ach.achievements.map((a) => (
            <div key={a.id} data-testid={`ach-${a.id}`}
              className={"rounded-xl border p-3 " + (a.unlocked ? "bg-[#FBBF24]/10 border-[#FBBF24]/30" : "bg-[#F6F8FC] border-[#E4EAF3]")}>
              <div className={"w-8 h-8 rounded-full flex items-center justify-center mb-2 " + (a.unlocked ? "bg-[#F59E0B] text-white" : "bg-white text-[#0A1A2F]/40 border border-[#E4EAF3]")}>
                {a.unlocked ? <Star className="w-4 h-4" fill="white" /> : <Lock className="w-4 h-4" />}
              </div>
              <div className="font-bold text-sm text-[#0A1A2F]">{a.title}</div>
              <div className="text-[11px] text-[#0A1A2F]/60">{a.desc}</div>
              <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
                <div className={"h-full rounded-full " + (a.unlocked ? "bg-[#F59E0B]" : "bg-[#1E88FF]")} style={{ width: `${a.progress_pct}%` }} />
              </div>
              <div className="text-[10px] text-[#0A1A2F]/50 mt-1">{a.current} / {a.goal}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function MiniStat({ label, value, icon, testId }) {
  return (
    <div data-testid={testId} className="bg-white/10 rounded-xl p-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-white/70 inline-flex items-center gap-1">{icon}{label}</div>
      <div className="text-lg font-black">{value}</div>
    </div>
  );
}
